// eslint-disable-next-line no-control-regex -- control chars are genuinely invalid in filenames
const INVALID_FILENAME_CHARS = /[\\/:*?"<>|\x00-\x1f]/g
// Only used to test whether a name is made up entirely of invalid/blank
// characters (no `g` flag — this one is never used with `.replace`).
// eslint-disable-next-line no-control-regex -- same control-char set as above
const ONLY_INVALID_OR_BLANK = /^[\\/:*?"<>|\x00-\x1f. ]*$/
const TRAILING_DOTS_OR_SPACES = /[. ]+$/
const LEADING_SPACES = /^[ ]+/
const MAX_FILENAME_LENGTH = 150

// Fullwidth Unicode lookalikes for the ASCII punctuation that's invalid in a
// filename on at least one OS — visually near-identical to the original
// character, so a title like "auto -> done" still reads as "auto -＞ done"
// instead of the original character disappearing into a generic "_"
// (BRAI-214: testers expect the downloaded filename to still look like the
// meeting title). Control characters have no sensible lookalike and fall
// back to "_".
const LOOKALIKE_REPLACEMENTS: Record<string, string> = {
  '\\': '＼',
  '/': '／',
  ':': '：',
  '*': '＊',
  '?': '？',
  '"': '＂',
  '<': '＜',
  '>': '＞',
  '|': '｜',
}

/**
 * Sanitize a user-provided string (e.g. a meeting title) into a filename that
 * is safe to use in `<a download>` on Windows/macOS/Linux, while preserving
 * Unicode characters (Vietnamese diacritics, Japanese kanji/kana, etc.) since
 * browsers fully support Unicode in the `download` attribute.
 *
 * Characters that are invalid in filenames (`\ / : * ? " < > |`) are replaced
 * with a visually similar fullwidth Unicode character instead of a generic
 * `_`, so the result still reads like the original name; control characters
 * (no sensible lookalike) fall back to `_`. Leading/trailing spaces and
 * trailing dots are trimmed (Windows disallows filenames ending with a dot or
 * space), and the result is capped to a safe length.
 *
 * @param name Raw string to sanitize, typically a meeting title.
 * @param fallback Value returned when sanitizing yields no meaningful name
 *   (empty, or made up entirely of invalid/blank characters).
 * @returns A filesystem-safe filename (without extension) that still keeps
 *   non-ASCII characters intact.
 * @example
 * sanitizeFilename('Họp nhóm dự án') // 'Họp nhóm dự án'
 * sanitizeFilename('議事録: 4/1') // '議事録： 4／1'
 * sanitizeFilename('///') // 'meeting'
 */
export function sanitizeFilename(name: string, fallback = 'meeting'): string {
  if (ONLY_INVALID_OR_BLANK.test(name)) return fallback

  const sanitized = name
    .replace(INVALID_FILENAME_CHARS, (char) => LOOKALIKE_REPLACEMENTS[char] ?? '_')
    .replace(LEADING_SPACES, '')
    .slice(0, MAX_FILENAME_LENGTH)
    .replace(TRAILING_DOTS_OR_SPACES, '')

  return sanitized || fallback
}
